import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../modules/PageWrapper";
import { BsChevronRight } from "react-icons/bs";
import LoanRepaymentTable from "./LoanRepaymentTable";
import { MemberLoanRepaymentHeader } from "../../../utils/TableHeader";
import LoanManagementCard from "../modules/LoanManagementCard";
import MemberLoanRepaymentReceiptModal from "./modals/MemberLoanRepaymentReceiptModal";
import { useUser } from "../../../hooks";
import { 
  useGetOverdueRepaymentLoanListQuery,
  useGetMemberLoanSummaryQuery,
  useGetMemberGuarantorRequestsSummaryQuery,
} from "../../../store/member/member-loan";

export default function LoanManagement() {
  const navigate = useNavigate();
  const user = useUser();
  const [overdueRepaymentPage, setOverdueRepaymentPage] = useState(1);
  const [modalPage, setModalPage] = useState("");
  const [selectedRepaymentId, setSelectedRepaymentId] = useState(null);

  // Fetch loan summary from backend
  const { data: loanSummary, isLoading: isLoadingLoanSummary } = useGetMemberLoanSummaryQuery();
  const {
    data: guarantorSummary,
    isLoading: isLoadingGuarantorSummary,
  } = useGetMemberGuarantorRequestsSummaryQuery();

  const pendingGuarantorCount = guarantorSummary?.pending_count || 0;

  const { data: overdueRepayments, isLoading: isLoadingOverdueRepayments } =
    useGetOverdueRepaymentLoanListQuery({
      page: overdueRepaymentPage,
      member_id: user?.member_id,
    });

  const MemberLateContributions = () => {
    return [
      {
        name: "Check your recently applied loan status",
        contributions: "Loan status",
        image: "/MemberSide/Icons/dashboard3.svg",
        percentage: "80%",
        bgColor: "#A8E1C8",
        color: "#318C64",
        onclick: "/member/loan_management/loan_status",
      },
      {
        name: `You have ${loanSummary?.pending_repayment?.count || 0} pending loan repayment${(loanSummary?.pending_repayment?.count || 0) !== 1 ? 's' : ''}`,
        contributions: "Loan Repayment",
        image: "/MemberSide/Icons/dashboard5.svg",
        percentage: "80%",
        bgColor: "var(--color-primary-soft)",
        color: "var(--color-primary)",
        onclick: "/member/loan_management/loan_repayment",
      },
      {
        name:
          pendingGuarantorCount > 0
            ? `You have ${pendingGuarantorCount} pending guarantor request${
                pendingGuarantorCount !== 1 ? "s" : ""
              }`
            : "Review loans where you were selected as guarantor",
        contributions: "Guarantor requests",
        image: "/MemberSide/Icons/dashboard3.svg",
        percentage: "80%",
        bgColor: "rgba(49, 119, 140, 0.12)",
        color: "#31778C",
        onclick: "/member/loan_management/guarantor_requests",
        badge: pendingGuarantorCount,
      },
    ];
  };

  const CardSkeleton = () => (
    <div className=" space-x-4 rounded-[0.625rem] shadow3 flex items-center pointer px-[2.42rem] py-[1.25rem] ">
      <div
        style={{
          width: "2.5rem",
          height: "2.5rem",
          borderRadius: "9999px",
          background: "#f1f1f1",
        }}
      />
      <div className="w-full space-y-2 ">
        <div
          style={{
            height: "14px",
            width: "50%",
            background: "#e5e5e5",
            borderRadius: "6px",
          }}
        />
        <div
          style={{
            height: "12px",
            width: "70%",
            background: "#f1f1f1",
            borderRadius: "6px",
          }}
        />
      </div>
      <div>
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "9999px",
            background: "#f1f1f1",
          }}
        />
      </div>
    </div>
  );

  const getTableDropDown = (status) => {
    const actions = [];
    const normalizedStatus = (status || "").toUpperCase();

    if (normalizedStatus === "PAID" || normalizedStatus === "COMPLETED") {
      actions.push({
        name: "View payment receipt",
        action: (data) => {
          if (data?.id) {
            setSelectedRepaymentId(data.id);
            setModalPage("View Payment Receipt");
          }
        },
      });
    } else {
      actions.push({
        name: "Pay now",
        action: (data) => {
          const loanRequestId = data?.loan_request_id || data?.loan_request?.id;
          const basePath = "/member/loan_management/loan_repayment";
          if (loanRequestId) {
            navigate(`${basePath}/pay/${data?.id}?loan_request_id=${loanRequestId}`);
          } else {
            navigate(`${basePath}/pay/${data?.id}`);
          }
        },
      });
    }
    return actions;
  };

  return (
    <PageWrapper title={"Loan Management"}>
      <section className="mt-6 space-y-6 ">
        <div className="w-full flex justify-between items-center flex-wrap gap-12">
          <div className="flex-1">
            <LoanManagementCard />
          </div>
          <section className=" space-y-5 flex-1">
            {isLoadingLoanSummary || isLoadingGuarantorSummary
              ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              )
              : MemberLateContributions()?.map((item, i) => (
                <div
                  onClick={() => navigate(item?.onclick)}
                  className=" space-x-4 rounded-[0.625rem] shadow3 flex items-center pointer px-[2.42rem] py-[1.25rem] "
                  key={i}
                  data-tour={
                    i === 0
                      ? "member-loan-status-card"
                      : i === 1
                        ? "member-loan-repay-card"
                        : i === 2
                          ? "member-guarantor-requests-card"
                          : undefined
                  }
                >
                  <img
                    src={item?.image}
                    alt="img"
                    className="w-[2.5rem] h-[2.5rem]"
                  />

                  <div className="w-full space-y-2 ">
                    <h4 className="d-flex align-items-center gap-2">
                      {item?.contributions}
                      {item?.badge > 0 && (
                        <span
                          className="rounded-pill px-2 py-0 text-white"
                          style={{
                            background: "#FF4343",
                            fontSize: 12,
                            minWidth: 22,
                            textAlign: "center",
                          }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </h4>
                    <p className={`text-muted h50 font-1 ${item?.nameClassName}`}>
                      {item?.name}
                    </p>
                  </div>
                  <div>
                    <BsChevronRight size={20} />
                  </div>
                </div>
              ))}
          </section>
        </div>
      </section>
      <section className="mt-[4rem] bg-white rounded-[1.25rem] ">
        <h4 className="font-bold text-base text-[#45464E] pt-[2rem] px-[2rem] ">
          Overdue Loans
        </h4>
        <LoanRepaymentTable
          name="overdue repayment"
          TableData={overdueRepayments?.data?.company_loan_requests || []}
          pagination={overdueRepayments?.data?.pagination}
          loading={isLoadingOverdueRepayments}
          setPageNumber={setOverdueRepaymentPage}
          column={MemberLoanRepaymentHeader(getTableDropDown)}
        />
      </section>

      {modalPage === "View Payment Receipt" && selectedRepaymentId && (
        <MemberLoanRepaymentReceiptModal
          repaymentId={selectedRepaymentId}
          setModalPage={setModalPage}
        />
      )}
    </PageWrapper>
  );
}
